/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'commission-payment-summary',
	templateUrl: './commission-payment-summary.component.html'
})

/**
 * A component representing a wizard step to display the commission payment summary.
 *
 * @export
 * @class CommissionPaymentSummaryComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class CommissionPaymentSummaryComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the commission payment summary component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof CommissionPaymentSummaryComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public currentData: any;

	/**
	 * Gets or sets the agency id associated to the commission payment.
	 *
	 * @type {number}
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public agencyId: number;

	/**
	 * Gets the workflow action being executed to make the commission payment.
	 *
	 * @type {string}
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public readonly workflowActionName: string =
		'ProcessCommissionPayment';

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public ngOnInit(): void
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData.data;

		this.agencyId =
			this.currentData.id;

		this.context.source.addToNext(
			this.makePayment.bind(this));

		this.context.source.validStepChanged(true);

		this.context.source.wizardStepLoading = false;
		this.context.source.changeDetectorReference.detectChanges();
	}

	/**
	 * Processes the commission payment using the current data.
	 *
	 * @async
	 * @memberof CommissionPaymentSummaryComponent
	 */
	public async makePayment(): Promise<void>
	{
		let queryString: string =
			`?transactionId=${this.currentData.transactionId}`
				+ `&amount=${this.currentData.amount}`
				+ `&commissionTransactionId=${this.currentData.commissionTransactionId}`
				+ `&transactionDate=${this.currentData.transactionDate}`;

		if (!AnyHelper.isNull(this.currentData.referenceId))
		{
			queryString += `&referenceId=${this.currentData.referenceId}`
				+ `&referenceDescription=${this.currentData.reference.description}`;
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups
				.agencies;

		await this.activityService.handleActivity(
			new Activity(
				this.entityInstanceApiService
					.executeAction(
						this.agencyId,
						this.workflowActionName,
						null,
						queryString),
				'<strong>Recording commission payment</strong>',
				'<strong>Commission payment recorded</strong>',
				'The commission payment has been recorded for the agency.',
				'The commission payment was not recorded.'));

		this.navigateToAgencyDashboard(
			this.agencyId);
	}

	/**
	 * This will navigate to the agency dashboard using the provided agency id.
	 *
	 * @param {number} agencyId
	 * The agency id to navigate to.
	 * @memberof CommissionPaymentSummaryComponent
	 */
	private navigateToAgencyDashboard(
		agencyId: number): void
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				InsuranceConstants.insuranceEntityTypeGroups.agencies,
				AppConstants.viewTypes.edit,
				agencyId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}
}